/**
 * The four setup steps of a business unit, and how far along one is.
 *
 * Only the name is required; the other three gate real behaviour — without the signature the BU
 * generates none, without the flag key it cannot be targeted in Flagsmith, and without an icon it
 * falls back to a generic one. The x/4 indicator appears in three places (the BU modal, the BU
 * list and the overview card) and they must all agree, so the calculation lives here.
 *
 * @module Pages/BUs/BUManager
 */

import type { Tenant, TenantSetupSteps } from '../Components/BUPlatformList/BUPlatformList.types';

/** One step, as the indicator draws it. */
export interface SetupStep {
  key: keyof TenantSetupSteps;
  label: string;
  done: boolean;
}

/** Colour of the whole indicator: green at 4/4, red at 1 or fewer, amber in between. */
export type SetupTone = 'success' | 'warning' | 'danger';

export interface SetupProgress {
  steps: SetupStep[];
  /** How many of the four are done. */
  done: number;
  tone: SetupTone;
  /** e.g. "3/4 passos". */
  label: string;
  /** e.g. "Falta Ícone, Key da flag" — or the completion message. */
  pendingText: string;
  isComplete: boolean;
}

const STEP_LABELS: { key: keyof TenantSetupSteps; label: string }[] = [
  { key: 'name', label: 'Nome' },
  { key: 'signature', label: 'Assinatura' },
  { key: 'icon', label: 'Ícone' },
  { key: 'flagKey', label: 'Key da flag' },
];

/** Green only when everything is done; red reserved for a BU that has barely been started. */
export function getSetupTone(done: number): SetupTone {
  if (done >= 4) return 'success';
  if (done <= 1) return 'danger';
  return 'warning';
}

/**
 * Derives the four steps for a tenant.
 *
 * Uses the server-computed `setupSteps` when present. The fallback exists for a deployment that
 * predates the field and infers each step from the data that backs it.
 */
export function getSetupProgress(tenant: Tenant): SetupProgress {
  const serverSteps = tenant.setupSteps;

  const resolved: TenantSetupSteps = serverSteps ?? {
    name: Boolean(tenant.name?.trim()),
    signature: Boolean(tenant.signatureSubject) || Boolean(tenant.signatureConfig),
    icon: Boolean(tenant.iconName),
    flagKey: Boolean(tenant.flagsmithEnvironmentKey),
  };

  const steps = STEP_LABELS.map(({ key, label }) => ({ key, label, done: resolved[key] }));
  const done = steps.filter(step => step.done).length;
  const pending = steps.filter(step => !step.done).map(step => step.label);

  return {
    steps,
    done,
    tone: getSetupTone(done),
    label: `${done}/4 passos`,
    pendingText: pending.length > 0 ? `Falta ${pending.join(', ')}` : 'Configuração completa',
    isComplete: done === 4,
  };
}

/** Token pair for a tone, so every place that draws the indicator uses the same colours. */
export function getToneColors(tone: SetupTone): { color: string; background: string } {
  switch (tone) {
    case 'success':
      return { color: 'var(--color-success)', background: 'var(--color-success-subtle)' };
    case 'danger':
      return { color: 'var(--color-danger)', background: 'var(--color-danger-subtle)' };
    default:
      return { color: 'var(--gl-yellow-dark)', background: 'var(--color-warning-subtle)' };
  }
}
